/* eslint-disable react/no-did-update-set-state */

import React from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import { memoize } from 'lodash';

import { getStepPath } from '../../utils/globalUtils';

const baseCls = 'checkout-routes-transition';

class TransitionSwitch extends React.PureComponent {
  /**
   * Фолбэк высоты компонента
   */
  // eslint-disable-next-line react/sort-comp
  static HEIGHT_FALLBACK = 300;

  /**
   * Направление
   */
  static DIRECTION = {
    FORWARD: 'forward',
    BACK: 'back',
  };

  /**
   * Мемоизированный стиль для корневого элемента
   */
  static memoizedStyleRoot = memoize(({ isTransition, transitionHeight }) => ({
    minHeight: isTransition ? transitionHeight : 'auto',
  }));

  /**
   * Мемоизированный стиль для предыдущего элемента
   */
  static memoizedStyleBack = memoize(({ isTransition, transitionHeight }) => ({
    marginTop: isTransition ? `-${transitionHeight}px` : 0,
  }));

  /**
   * Мемоизированный стиль для обертки
   */
  static memoizedStyleWrapper = memoize(
    ({ isTransition, direction, transitionHeight, transitionTime }) => {
      const translate = isTransition ? `${direction}${transitionHeight}px` : 0;
      return {
        transition: isTransition ? `all ease ${transitionTime}ms` : null,
        transform: `translateY(${translate})`,
      };
    },
  );

  /**
   * Высоты роутов
   */
  routeHeight = {};

  constructor(props) {
    super(props);

    this.state = {
      currentPath: props.currentPath,
      nextPath: null,
      direction: null,
    };

    this.getComponent = this.getComponent.bind(this);
    this.currentRef = React.createRef();
  }

  static getDerivedStateFromProps(props, state) {
    const { currentPath } = state;
    const nextPath = props.currentPath;

    if (nextPath !== currentPath) {
      const { routes } = props;
      const direction = TransitionSwitch.getRouteDirection(
        currentPath,
        nextPath,
        routes,
      );

      return {
        nextPath,
        direction,
      };
    }

    return null;
  }

  /**
   * Возвращает индекс для роута
   * @returns {nubmer}
   */
  static getRouteIndex = (path, routes) =>
    routes.findIndex((item) => item.path === path);

  /**
   * Возвращает направление
   * @param {number} nextIndex
   * @param {number} currentIndex
   * @returns {string} "forward", "back"
   */
  static getDirection = (nextIndex, currentIndex) => {
    const direction =
      nextIndex > currentIndex
        ? TransitionSwitch.DIRECTION.FORWARD
        : TransitionSwitch.DIRECTION.BACK;

    return direction;
  };

  /**
   * Возвращает направление для роута
   * @param {string} currentPath
   * @param {string} nextPath
   * @returns {string} "forward", "back"
   */
  static getRouteDirection = (currentPath, nextPath, routes) => {
    const currentIndex = TransitionSwitch.getRouteIndex(currentPath, routes);
    const nextIndex = TransitionSwitch.getRouteIndex(nextPath, routes);
    const direction = TransitionSwitch.getDirection(nextIndex, currentIndex);

    return direction;
  };

  componentDidUpdate(prevProps, prevState) {
    const { nextPath, currentPath } = this.state;
    const { transitionTime, onTransitionStart, onTransitionEnd } = this.props;

    if (nextPath && prevState.nextPath !== nextPath) {
      if (this.isSameStep) {
        this.setState({
          currentPath: nextPath,
          nextPath: null,
        });
      } else {
        window.scrollTo(0, 0);
        this.routeHeight[currentPath] = this.height;
        onTransitionStart();
        setTimeout(() => {
          this.setState(
            {
              currentPath: nextPath,
              nextPath: null,
            },
            onTransitionEnd,
          );
        }, transitionTime);
      }
    }
  }

  get isSameStep() {
    const { nextPath, currentPath } = this.state;
    return getStepPath(currentPath) === getStepPath(nextPath);
  }

  /**
   * Возвращает компонент по указанному пути
   * @param {string} path
   */
  getComponent(path) {
    const { routes } = this.props;
    const route = routes.find((item) => item.path === path);

    if (!route) {
      return null;
    }

    return route.component;
  }

  /**
   * Возвращает активный компонент
   */
  get currentComponent() {
    const { currentPath } = this.state;
    const Component = this.getComponent(currentPath);

    if (!Component) {
      return null;
    }

    return <Component />;
  }

  /**
   * Возвращает следующий компонент
   */
  get nextComponent() {
    const { nextPath } = this.state;

    if (!nextPath) {
      return null;
    }

    const Component = this.getComponent(nextPath);

    if (!Component) {
      return null;
    }

    return <Component />;
  }

  /**
   * Предыдущий шаг
   */
  get isForward() {
    return !this.isBack;
  }

  /**
   * Следующий шаг
   */
  get isBack() {
    const { direction } = this.state;
    return direction === TransitionSwitch.DIRECTION.BACK;
  }

  /**
   * Смена шага
   */
  get isTransition() {
    const { currentPath, nextPath } = this.state;
    return !this.isSameStep && currentPath && nextPath;
  }

  /**
   * Возвращает высоту активного компонента
   * или фолбэчится
   */
  get height() {
    return this.currentRef.current
      ? this.currentRef.current.offsetHeight
      : TransitionSwitch.HEIGHT_FALLBACK;
  }

  /**
   * Возвращает высоту, на которую необходимо сделать транлейт
   */
  get transitionHeight() {
    const { nextPath } = this.state;
    return this.isBack ? this.routeHeight[nextPath] : this.height;
  }

  /**
   * Стиль для предыдущего компонента
   */
  get styleBack() {
    return TransitionSwitch.memoizedStyleBack({
      isTransition: this.isTransition,
      transitionHeight: this.transitionHeight,
    });
  }

  /**
   * Стиль для следующего компонента
   */
  // get styleForward() {
  //   return null;
  //   return {
  //     opacity: this.isTransition ? 1 : 0,
  //   };
  // }

  /**
   * Стиль для обертки, дает эффект скролла
   */
  get styleWrapper() {
    const direction = this.isForward ? '-' : '';
    const { transitionTime } = this.props;

    return TransitionSwitch.memoizedStyleWrapper({
      isTransition: this.isTransition,
      direction,
      transitionHeight: this.transitionHeight,
      transitionTime,
    });
  }

  /**
   * Задает минимальную высоту для контейнера
   */
  get styleRoot() {
    return TransitionSwitch.memoizedStyleRoot({
      isTransition: this.isTransition,
      transitionHeight: this.transitionHeight,
    });
  }

  render() {
    return (
      <div
        className={cn(baseCls, { scroll: this.isTransition })}
        style={this.styleRoot}
      >
        <div style={this.styleWrapper}>
          {this.isBack && this.isTransition && (
            <div className={`${baseCls}__prev`} style={this.styleBack}>
              {this.nextComponent}
            </div>
          )}
          <div ref={this.currentRef} className={`${baseCls}__current`}>
            {this.currentComponent}
          </div>
          {this.isForward && this.isTransition && (
            <div className={`${baseCls}__next`}>{this.nextComponent}</div>
          )}
        </div>
      </div>
    );
  }
}

TransitionSwitch.defaultProps = {
  transitionTime: 350,
  onTransitionStart: Function.prototype,
  onTransitionEnd: Function.prototype,
};

TransitionSwitch.propTypes = {
  currentPath: PropTypes.string.isRequired,
  routes: PropTypes.arrayOf(
    PropTypes.shape({
      path: PropTypes.string.isRequired,
      component: PropTypes.func,
    }),
  ).isRequired,
  transitionTime: PropTypes.number,
  onTransitionStart: PropTypes.func,
  onTransitionEnd: PropTypes.func,
};

export default TransitionSwitch;
